/**
 * Template Service
 * 
 * Handles loading, rendering, and PDF generation for complaint templates
 */

import JSPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Template mapping - templates are in public/templates folder
const TEMPLATE_MAP = {
  'closer-template': '/templates/closer-template.html',
  'vague': '/templates/vague-template.html',
  'general': '/templates/general-template.html',
  'non-specific': '/templates/non-specific-template.html',
  'no-template': null, // No template for this option
};

/**
 * Load template HTML content
 * @param {string} templateName - Name of the template
 * @returns {Promise<string>} Template HTML content
 */
export const loadTemplate = async (templateName) => {
  try {
    const templatePath = TEMPLATE_MAP[templateName];
    if (!templatePath) {
      throw new Error(`Template ${templateName} not found`);
    }

    // For React apps, we'll need to import the template content directly
    // Since we can't fetch from public folder easily, we'll use a different approach
    const response = await fetch(templatePath);
    if (!response.ok) {
      throw new Error(`Failed to load template: ${templateName}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error loading template:', error);
    // Fallback: return template content directly from memory
    return getTemplateContent(templateName);
  }
};

/**
 * Get template content from memory (fallback)
 * @param {string} templateName - Name of the template
 * @returns {string} Template HTML content
 */
const getTemplateContent = (templateName) => {
  // This will be populated with actual template content
  // For now, return a simple template
  const templates = {
    'closer-template': `<!DOCTYPE html>
<html>
<head><title>Closer Template</title></head>
<body>
  <h1>Complaint Closure Notice</h1>
  <p>Dear {{complainant_name}},</p>
  <p>Your complaint {{complaint_id}} has been closed.</p>
  <p>{{remarks}}</p>
</body>
</html>`,
    'vague': `<!DOCTYPE html>
<html>
<head><title>Vague Template</title></head>
<body>
  <h1>Complaint Review - Vague</h1>
  <p>Dear {{complainant_name}},</p>
  <p>Your complaint {{complaint_id}} has been marked as vague.</p>
  <p>{{remarks}}</p>
</body>
</html>`,
    'general': `<!DOCTYPE html>
<html>
<head><title>General Template</title></head>
<body>
  <h1>General Complaint Notice</h1>
  <p>Dear {{complainant_name}},</p>
  <p>Your complaint {{complaint_id}} has been reviewed.</p>
  <p>{{remarks}}</p>
</body>
</html>`,
    'non-specific': `<!DOCTYPE html>
<html>
<head><title>Non-Specific Template</title></head>
<body>
  <h1>Non-Specific Complaint Notice</h1>
  <p>Dear {{complainant_name}},</p>
  <p>Your complaint {{complaint_id}} has been marked as non-specific.</p>
  <p>{{remarks}}</p>
</body>
</html>`,
  };
  return templates[templateName] || templates['general'];
};

/**
 * Replace placeholders in template with actual data
 * @param {string} template - Template HTML content
 * @param {object} data - Data to replace placeholders
 * @returns {string} Rendered HTML
 */
export const renderTemplate = (template, data) => {
  let rendered = template;
  
  // Replace all placeholders
  Object.keys(data).forEach(key => {
    const placeholder = `{{${key}}}`;
    const value = data[key] || '';
    rendered = rendered.replace(new RegExp(placeholder, 'g'), value);
  });
  
  return rendered;
};

/**
 * Generate PDF from HTML content
 * @param {string} htmlContent - HTML content to convert to PDF
 * @param {string} filename - Name of the PDF file
 * @returns {Promise<void>}
 */
export const generatePDF = async (htmlContent, filename = 'complaint-document.pdf') => {
  try {
    // PDF content area dimensions (with margins)
    const pdfMargin = 20; // 20mm margin on all sides
    const pdfWidth = 210; // A4 width in mm
    const contentWidthMM = pdfWidth - (pdfMargin * 2); // 170mm content width
    const contentWidthPX = (contentWidthMM / 25.4) * 96; // Convert to pixels at 96dpi (~642px)
    
    // Use iframe to render HTML with all styles properly applied
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = `${contentWidthPX}px`;
    iframe.style.height = '2000px'; // Tall enough for content
    iframe.style.border = 'none';
    iframe.style.backgroundColor = '#ffffff';
    
    // Wait for iframe to load and write content
    await new Promise((resolve) => {
      iframe.onload = () => {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const iframeHtml = iframeDoc.documentElement;
        const iframeBody = iframeDoc.body;
        
        // Set iframe document width to match content width
        iframeHtml.style.width = `${contentWidthPX}px`;
        iframeHtml.style.margin = '0';
        iframeHtml.style.padding = '0';
        iframeBody.style.margin = '0';
        // Don't override body padding - let template styles handle it
        iframeBody.style.width = '100%';
        iframeBody.style.boxSizing = 'border-box';
        
        resolve();
      };
      document.body.appendChild(iframe);
      
      // Write HTML content to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
    });
    
    // Get iframe document and body (after load)
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    const iframeBody = iframeDoc.body;
    
    // Wait for styles to apply and content to render
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Convert iframe body to canvas
    const canvas = await html2canvas(iframeBody, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: iframeBody.scrollWidth || contentWidthPX,
      height: iframeBody.scrollHeight,
      windowWidth: iframeBody.scrollWidth || contentWidthPX,
      windowHeight: iframeBody.scrollHeight,
    });
    
    // Clean up
    document.body.removeChild(iframe);


    // PDF dimensions
    const pdfHeight = 297; // A4 height in mm
    const contentHeight = pdfHeight - (pdfMargin * 2); // Available content height per page: 257mm

    // Calculate image dimensions for PDF
    // Canvas was rendered at scale 2, so actual content dimensions are canvas.width/2 x canvas.height/2
    // Convert pixels to mm: 1 inch = 25.4mm, and at 96dpi: 1px = 25.4/96 mm ≈ 0.2646mm
    const pxToMM = 25.4 / 96; // Conversion factor from pixels to mm
    const actualWidthMM = (canvas.width / 2) * pxToMM; // Actual width in mm (accounting for scale 2)
    const actualHeightMM = (canvas.height / 2) * pxToMM; // Actual height in mm
    
    // Scale to fit content width while maintaining aspect ratio
    const scaleRatio = contentWidthMM / actualWidthMM;
    const imgWidth = contentWidthMM;
    const imgHeight = actualHeightMM * scaleRatio;

    // Create PDF
    const pdf = new JSPDF('p', 'mm', 'a4');
    pdf.setProperties({
      title: filename.replace('.pdf', ''),
      subject: 'Complaint Document',
    });
    
    if (imgHeight <= contentHeight) {
      // Single page - fits on one page
      pdf.addImage(
        canvas.toDataURL('image/png', 1.0),
        'PNG',
        pdfMargin,
        pdfMargin,
        imgWidth,
        imgHeight
      );
    } else {
      // Multiple pages needed
      let sourceY = 0;
      let remainingHeight = imgHeight;
      let pageNum = 0;
      
      while (remainingHeight > 0) {
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        // Calculate how much content fits on this page
        const pageContentHeight = Math.min(remainingHeight, contentHeight);
        // Calculate the source height in canvas pixels (accounting for scale 2)
        // imgHeight is the total height in mm, pageContentHeight is the height for this page in mm
        const sourceHeightPx = (pageContentHeight / imgHeight) * canvas.height;
        
        // Create a canvas for this page slice
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(sourceHeightPx);
        const pageCtx = pageCanvas.getContext('2d');
        
        // Fill with white background
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        
        // Draw the portion of the original canvas for this page
        pageCtx.drawImage(
          canvas,
          0,
          Math.floor(sourceY),
          canvas.width,
          Math.ceil(sourceHeightPx),
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );
        
        // Add to PDF
        pdf.addImage(
          pageCanvas.toDataURL('image/png', 1.0),
          'PNG',
          pdfMargin,
          pdfMargin,
          imgWidth,
          pageContentHeight
        );
        
        // Update for next iteration
        sourceY += sourceHeightPx;
        remainingHeight -= pageContentHeight;
        pageNum++;
      }
    }

    // Download PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Preview template in a new window
 * @param {string} htmlContent - HTML content to preview
 */
export const previewTemplate = (htmlContent) => {
  const newWindow = window.open('', '_blank');
  newWindow.document.write(htmlContent);
  newWindow.document.close();
};

/**
 * Get available templates for a complaint type
 * @param {string} complaintType - Type of complaint
 * @returns {array} Array of available template names
 */
export const getTemplatesForType = (complaintType) => {
  if (!complaintType) return [];
  
  const normalizedType = String(complaintType).toLowerCase().trim();
  
  if (normalizedType === 'anonymous') {
    return [
      { value: 'closer-template', label: 'Closer Template' }
    ];
  } else if (normalizedType === 'no vigilance angle') {
    return [
      { value: 'vague', label: 'Vague' },
      { value: 'general', label: 'General' },
      { value: 'non-specific', label: 'Non Specific' }
    ];
  } else if (normalizedType === 'has vigilance angle') {
    return [
      { value: 'no-template', label: 'No Template Selected' }
    ];
  }
  
  return [];
};

const templateService = {
  loadTemplate,
  renderTemplate,
  generatePDF,
  previewTemplate,
  getTemplatesForType,
};

export default templateService;

